import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { WA_MESSAGES, COVERAGE_FACTS } from "@/lib/site";

export default function NewCompanyDatabase() {
  const path = "/new-company-database";
  const title = "New Company Database India | MCA + GST Combined | Registry Feed";
  const description = "Combined MCA and GST new company database — both data streams in one subscription. Daily fresh, pan-India. Best value for B2B sales teams.";
  const keywords = "mca and gst data, mca + gst combined database, new company leads india, new business leads india, newly registered companies list india";
  const bc = [{ name: "Home", path: "/" }, { name: "New Company Database", path }];
  const jsonLd = pageSchema({ title, description, path, breadcrumbs: bc });
  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} keywords={keywords} path={path} jsonLd={jsonLd} />

      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl">
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            New Company Database India — MCA + GST Combined Feed
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Get MCA and GST data in a single subscription. Registry Feed's MCA + GST combined database gives your sales team the complete newly registered companies list India produces every day — new incorporations and new GST registrations together — so you never miss new company leads India-wide. One dashboard, one download, the freshest new business leads India has.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.new} label="Get New Company Data" testid="new-cta-whatsapp" />
            <Link to="/sample-data"><Button size="lg" variant="outline">Request Sample</Button></Link>
            <Link to="/pricing"><Button size="lg" variant="ghost">Get Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">What is inside the feed?</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="font-heading font-semibold">MCA-registered companies</div>
              <p className="mt-2 text-sm text-muted-foreground">Freshly incorporated Pvt Ltd, LLP, OPC and Public companies from every ROC jurisdiction.</p>
              <Link to="/mca-database" className="mt-3 inline-flex text-sm text-primary hover:underline">Explore MCA database →</Link>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="font-heading font-semibold">GST-registered businesses</div>
              <p className="mt-2 text-sm text-muted-foreground">Fresh GST registrations across proprietorships, partnerships and companies, filtered by state or city.</p>
              <Link to="/gst-database" className="mt-3 inline-flex text-sm text-primary hover:underline">Explore GST database →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Coverage &amp; delivery</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {COVERAGE_FACTS.map((c) => (
              <li key={c} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.new} label="Buy New Company Database" />
            <Link to="/pricing"><Button variant="outline">See Pricing</Button></Link>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
